import { useCallback, useMemo, useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarEvent } from "../types/event";
import { fetchEventList } from "./useEventManager";

/**
 * Events for the List and Cards views, paged A MONTH AT A TIME in both
 * directions.
 *
 * ## Why a page is a month, and why it is not one widening request
 *
 * The obvious version of "load more" is to widen the range and ask again —
 * 1 August to 30 September instead of 1 to 31 August. That breaks on this data.
 * The list endpoint takes `limit=10000` and August 2026 alone holds 8,072 events
 * in the Event collection, so a two-month window would cross the cap and come
 * back SILENTLY TRUNCATED: a list that looks complete and is not. It also
 * re-downloads every event already in hand to add one month to the end.
 *
 * One request per month keeps each one comfortably under the cap, and
 * useInfiniteQuery keeps the pages that have already been fetched.
 *
 * ## Bounded on purpose
 *
 * `maxPages` caps what is retained. At roughly 8,000 events a month, keeping
 * everything anyone scrolls past would grow without limit; past the cap, react
 * query drops the page at the far end. Scrolling forward through six months and
 * then turning round therefore refetches the first of them, which is the right
 * trade at this size — it is one request, and the alternative is holding tens of
 * thousands of documents no one is reading.
 *
 * The anchor month is part of the query key, so stepping the header's month
 * picker starts a fresh span there rather than paging out from wherever the last
 * one ended.
 */

/** Months retained at once. See the note above on why this is bounded. */
const MAX_MONTHS = 4;

/** `YYYY-MM` for a date, zero-padded so the keys sort as strings. */
export function monthKeyOf(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0");
	return `${date.getFullYear()}-${month}`;
}

/** The first and last instant of the month a `YYYY-MM` key names. */
export function monthBounds(key: string): { from: Date; to: Date } {
	const [year, month] = key.split("-").map(Number);
	return {
		from: new Date(year, month - 1, 1),
		// Day 0 of the next month is the last day of this one.
		to: new Date(year, month, 0),
	};
}

/** The key `by` months either side of `key`. */
export function shiftMonthKey(key: string, by: number): string {
	const [year, month] = key.split("-").map(Number);
	return monthKeyOf(new Date(year, month - 1 + by, 1));
}

interface MonthPage {
	month: string;
	events: CalendarEvent[];
}

export interface BrowseEvents {
	/** Every loaded month's events, oldest month first. */
	events: CalendarEvent[];
	/** The months held right now, in order — what the range label reads from. */
	months: string[];
	loading: boolean;
	loadingEarlier: boolean;
	loadingLater: boolean;
	loadEarlier: () => void;
	loadLater: () => void;
	/** Throw the paged months away and start again at the anchor month. */
	reset: () => void;
}

export function useBrowseEvents(
	anchorMonth: string,
	categories: string[],
	enabled: boolean,
	tags: string[] = [],
): BrowseEvents {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	// Sorted, so ticking A then B and ticking B then A share one cache entry —
	// the same rule useEventManager's key follows.
	const categoryKey = useMemo(
		() => [...categories].sort().join(","),
		[categories],
	);

	/* Lower-cased as well as sorted: the API matches tags case-insensitively, so
	   "Sport" and "sport" are one request and must not be two cache entries. */
	const tagKey = useMemo(
		() =>
			[...tags]
				.map((t) => t.toLocaleLowerCase("en-AU"))
				.sort()
				.join(","),
		[tags],
	);

	const queryKey = useMemo(
		() => ["events", "browse", "v1", anchorMonth, categoryKey, tagKey],
		[anchorMonth, categoryKey, tagKey],
	);

	const query = useInfiniteQuery<MonthPage>({
		queryKey,
		queryFn: async ({ pageParam }) => {
			const month = pageParam as string;
			const { from, to } = monthBounds(month);
			const res = await fetchEventList(instance, from, to, categories, tags);
			return { month, events: res.events as CalendarEvent[] };
		},
		initialPageParam: anchorMonth,
		/* Time has no last page, so these always answer. "Is there more?" is not a
		   question this data can be asked — the honest answer is that there is
		   always another month, and the person stops when they have found what
		   they came for. */
		getNextPageParam: (last) => shiftMonthKey(last.month, 1),
		getPreviousPageParam: (first) => shiftMonthKey(first.month, -1),
		maxPages: MAX_MONTHS,
		enabled: enabled && isAuthenticated,
	});

	const pages = useMemo(() => query.data?.pages ?? [], [query.data]);

	const events = useMemo(() => pages.flatMap((p) => p.events), [pages]);
	const months = useMemo(() => pages.map((p) => p.month), [pages]);

	/* The two loaders are handed to memoised components, so their identity has to
	   hold across renders or React.memo does nothing and every list re-renders on
	   every keystroke elsewhere on the page. Reading the query through a ref keeps
	   the callbacks stable while still acting on the current one. */
	const latest = useRef(query);
	latest.current = query;

	const loadEarlier = useCallback(() => {
		const q = latest.current;
		if (!q.isFetchingPreviousPage) void q.fetchPreviousPage();
	}, []);

	const loadLater = useCallback(() => {
		const q = latest.current;
		if (!q.isFetchingNextPage) void q.fetchNextPage();
	}, []);

	/**
	 * Back to a single page at the anchor month.
	 *
	 * Needed because `maxPages` EVICTS. Scrolling forward far enough drops the
	 * month you started in, and nothing else brings it back: the query key is the
	 * anchor month, and Today does not change that when today is already in the
	 * anchor month — so the key stays put, no refetch happens, and the pages in
	 * hand go on starting wherever scrolling left them. Resetting collapses them
	 * back to the anchor and fetches it again.
	 */
	const reset = useCallback(() => {
		void queryClient.resetQueries({ queryKey });
	}, [queryClient, queryKey]);

	return {
		events,
		months,
		loading: query.isPending,
		loadingEarlier: query.isFetchingPreviousPage,
		loadingLater: query.isFetchingNextPage,
		loadEarlier,
		loadLater,
		reset,
	};
}
